import * as Yup from "yup"

import type { AppLocalization } from "../../../../../modules/localization/appLocalization"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { PaymentMethodType } from "../../../../../modules/payments/paymentTypes"

import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    MeetingServicePriceListStatus,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { buildBookingSeedFromOrder } from "../resource/helpers"
import { buildMultipleDeliveriesState } from "./buildMultipleDeliveriesState"
import {
    CustomFieldsGroup,
    DeliveryTimesField,
    DeliveryWithCart,
    MeetingServiceConfig,
    DynamicConfigKey,
    DynamicConfigField,
    ExtendedDynamicConfigKey,
    BookingConfig,
    DeliveryFulfillmentType,
    ValidationType,
} from "./formTypes"
import {
    buildFieldSchema,
    defaultPaymentMethod,
    getBookingResourceWithTableSetup,
    INVALID_DELIVERIES,
    isAccountingDimensionKey,
    isConfigLockedField,
    isRenderableConfigField,
} from "./helpers"
import { mapConfigFieldsToValues } from "./mapConfigFieldsToValues"
import { defaultMultipleDeliveries } from "../../defaults"
import { getCreateOrderDialogTypeFlags } from "../../hooks/useCreateOrderDialogType"

export const DELIVERY_LOCATION_HAS_OPTIONS_FIELD = "__deliveryLocationHasOptions"

export const getAllConfigFields = (config?: MeetingServiceConfig | BookingConfig): DynamicConfigField[] => {
    if (!config?.sections?.length) return []

    return config.sections
        .filter((section) => section.showInDrawer !== false)
        .flatMap(({ groups }) => groups ?? [])
        .flatMap(({ fields }) => fields ?? [])
}

// Keys the config fully owns: hidden + predefined, so no user input can ever change them.
export const getConfigLockedKeys = (config?: MeetingServiceConfig | BookingConfig): ReadonlySet<string> =>
    new Set(
        getAllConfigFields(config)
            .filter(isConfigLockedField)
            .map(({ key }) => key),
    )

function ensurePaymentMethodValue(
    values: Partial<Record<ExtendedDynamicConfigKey, unknown>>,
    orderDetails?: MeetingServiceOrder,
): Partial<Record<ExtendedDynamicConfigKey, unknown>> {
    const raw = values[DynamicConfigKey.PAYMENT_METHOD]
    const rawValue = raw && typeof raw === "object" && "value" in raw ? (raw as { value?: unknown }).value : undefined

    if (rawValue === PaymentMethodType.INVOICE || rawValue === PaymentMethodType.CASH) {
        return values
    }

    const fromApi = orderDetails?.paymentDetails?.method

    return {
        ...values,
        [DynamicConfigKey.PAYMENT_METHOD]: fromApi ? { value: fromApi } : defaultPaymentMethod,
    }
}

export function getValuesFromConfig(
    user: SelectedMainUserState,
    dateTimeFactory: DateTimeFactory,
    dialogType: CreateOrderDialogType,
    config?: MeetingServiceConfig | BookingConfig,
    orderDetails?: MeetingServiceOrder,
    mode?: CreateOrderDialogMode,
    flowConfig?: CreateOrderDialogFlow,
) {
    const values = mapConfigFieldsToValues({
        fields: getAllConfigFields(config),
        user,
        dialogType,
        orderDetails,
        dateTimeFactory,
        flowConfig,
        mode,
    })

    // Always have PAYMENT_METHOD in Formik values so UI guards, validation, and payload behave
    // as if API had sent a payment method (INVOICE by default).
    return ensurePaymentMethodValue(values, orderDetails)
}

export function getCustomFieldsValues(
    customFields: CustomFieldsGroup,
    dateTimeFactory: DateTimeFactory,
    dialogType: CreateOrderDialogType,
    orderDetails?: MeetingServiceOrder,
    mode?: CreateOrderDialogMode,
    flowConfig?: CreateOrderDialogFlow,
) {
    let result = { ...customFields }

    // Always keep MULTIPLE_DELIVERIES in form values so validation/UI are stable across configs and flows.
    if (!result[DynamicConfigKey.MULTIPLE_DELIVERIES]) {
        result[DynamicConfigKey.MULTIPLE_DELIVERIES] = defaultMultipleDeliveries
    }

    if (orderDetails) {
        const { webshop, merchant, priceList, bookingDetails, deliveries, amount, created, updated } = orderDetails
        const workgroup = orderDetails.meetingDetails?.workgroup

        if (workgroup) result.workgroupId = String(workgroup.id)

        // Shared with the timeline draft so both seeds describe the same booking (see the helper).
        const bookingSeed = buildBookingSeedFromOrder({ orderDetails, dateTimeFactory, mode, flowConfig })

        if (bookingSeed.event) {
            result.event = bookingSeed.event
        }

        if (
            mode === CreateOrderDialogMode.EDIT &&
            bookingDetails &&
            bookingDetails.resources &&
            bookingDetails.resources.length > 0
        ) {
            result.fullResourcesList = bookingSeed.fullResourcesList
            result.resources = getBookingResourceWithTableSetup(bookingDetails.resources)
        }

        if (webshop && merchant && priceList) {
            const priceListValue = {
                merchantId: merchant.id,
                merchantName: merchant.name,
                priceListId: priceList.id,
                priceListName: priceList?.name || "",
                shopId: webshop.id,
                isOnlyOnePriceList: false,
                priceListIncludeVat: priceList.includeVat,
            }
            result.priceList = { item: priceListValue, status: MeetingServicePriceListStatus.VALIDATING }
            result.initPriceList = priceListValue
        }

        if (deliveries) {
            const md = buildMultipleDeliveriesState({
                mode,
                dialogType,
                flowConfig,
                orderDetails,
                dateTimeFactory,
            })
            if (md) {
                result[DynamicConfigKey.MULTIPLE_DELIVERIES] = md
            }
        }
        if (amount) result.initAmount = amount
        if (created) result.created = created
        if (updated) result.updated = updated
    }

    return result
}

// Makes the field optional if PAYMENT_METHOD.value !== “INVOICE”
const relaxUnlessInvoice = (sch: Yup.Schema<any>) =>
    sch.when(`${DynamicConfigKey.PAYMENT_METHOD}.value`, (pmValue: unknown, s: Yup.Schema<any>) => {
        const tag = pmValue == null ? undefined : String(pmValue).toUpperCase()
        return tag === "INVOICE" ? s : s.notRequired().nullable()
    })

type DeliveryLocationCheck = Readonly<{
    isSatisfied: boolean
    errorPath: DynamicConfigKey.DELIVERY_LOCATIONS | DynamicConfigKey.OTHER_DELIVERY_LOCATION
}>

// Evaluates whether a delivery/meeting location has been provided, and which field should own the
// validation error. The error must be attached to the field the user can actually see: when there are
// no predefined options the free-text field (OTHER_DELIVERY_LOCATION) is the visible one, otherwise the
// autocomplete (DELIVERY_LOCATIONS). This mirrors the rendering logic in OtherDeliveryLocationField.
function evaluateDeliveryLocation(value: Record<string, unknown>): DeliveryLocationCheck {
    const hasPredefinedOptions = value[DELIVERY_LOCATION_HAS_OPTIONS_FIELD] !== false

    const deliveryLocationId = value[DynamicConfigKey.DELIVERY_LOCATIONS]
    const otherLocation = value[DynamicConfigKey.OTHER_DELIVERY_LOCATION]
    const hasSelectedDeliveryLocation = deliveryLocationId != null && String(deliveryLocationId).trim() !== ""
    const hasOtherLocation = otherLocation != null && String(otherLocation).trim() !== ""

    // With predefined options either the dropdown selection or the free-text value satisfies the rule.
    // Without options only the free-text value can (the dropdown is hidden).
    const isSatisfied = hasPredefinedOptions ? hasSelectedDeliveryLocation || hasOtherLocation : hasOtherLocation
    const errorPath = hasPredefinedOptions
        ? DynamicConfigKey.DELIVERY_LOCATIONS
        : DynamicConfigKey.OTHER_DELIVERY_LOCATION

    return { isSatisfied, errorPath }
}

export function generateValidationSchema(
    dialogType: CreateOrderDialogType,
    l10n: AppLocalization,
    meetingServiceConfiguration?: MeetingServiceConfig,
): Yup.ObjectSchema<Record<ExtendedDynamicConfigKey, any>> {
    const shape: Partial<Record<ExtendedDynamicConfigKey, Yup.Schema<any>>> = {}
    const { isSpecialOrder } = getCreateOrderDialogTypeFlags(dialogType)

    const allFields: DynamicConfigField[] = meetingServiceConfiguration
        ? getAllConfigFields(meetingServiceConfiguration).filter(Boolean)
        : []

    const activeFieldKeys = new Set(allFields.filter(({ active }) => active).map(({ key }) => key))
    const renderableFieldKeys = new Set(allFields.filter(isRenderableConfigField).map(({ key }) => key))

    if (!isSpecialOrder) {
        allFields.forEach((config) => {
            const field = config.inputField
            const key = config.key
            const label = config.label || config.key

            if (
                key === DynamicConfigKey.DELIVERY_LOCATIONS ||
                key === DynamicConfigKey.OTHER_DELIVERY_LOCATION ||
                key === DynamicConfigKey.MULTIPLE_DELIVERIES ||
                key === DynamicConfigKey.SATELLITES
            )
                return

            if (!activeFieldKeys.has(key)) return
            if (field?.hidden) return
            shape[key] = buildFieldSchema(field, label, l10n)
        })

        // REGISTER_PARTICIPANTS is stored as an array. Yup.required() does not fail for empty arrays,
        // so we must enforce non-empty explicitly when required.
        // Hidden is checked alongside active: this block runs after the loop that skips hidden fields,
        // and nothing renders a hidden participants list for the user to fill.
        const participantsConfig = allFields.find((f) => f.key === DynamicConfigKey.REGISTER_PARTICIPANTS)
        if (participantsConfig && renderableFieldKeys.has(DynamicConfigKey.REGISTER_PARTICIPANTS)) {
            const label = participantsConfig.label || participantsConfig.key
            const isRequired =
                Boolean(participantsConfig.inputField?.isRequired) ||
                Boolean(participantsConfig.inputField?.validations?.some((v) => v.type === ValidationType.REQUIRED))

            if (isRequired) {
                const msg = l10n.getString("create-order-validation-required", { label })
                shape[DynamicConfigKey.REGISTER_PARTICIPANTS] = Yup.array().min(1, msg).required(msg)
            }
        }

        if (activeFieldKeys.has(DynamicConfigKey.DELIVERY_LOCATIONS)) {
            shape[DynamicConfigKey.DELIVERY_LOCATIONS] = Yup.string().nullable()
        }

        if (activeFieldKeys.has(DynamicConfigKey.OTHER_DELIVERY_LOCATION)) {
            shape[DynamicConfigKey.OTHER_DELIVERY_LOCATION] = Yup.string().nullable()
        }
    }

    // Always require MULTIPLE_DELIVERIES (independent of config.active) for meeting-service orders.
    shape[DynamicConfigKey.MULTIPLE_DELIVERIES] = Yup.mixed<DeliveryTimesField>()
        .test(
            "delivery-block-validation",
            l10n.getString("create-order-multiple-deliveries-date-and-time-required"),
            (value) => {
                if (!value) return false
                const hasDate = !!value.deliveryDate
                const hasAtLeastOneTime = Array.isArray(value.deliveryTimes) && value.deliveryTimes.length > 0
                return hasDate && hasAtLeastOneTime
            },
        )
        .test("delivery-has-products", function (value) {
            if (!value || !Array.isArray(value.deliveryTimes)) return true

            const invalidTimes = value.deliveryTimes
                .filter((delivery) => !delivery.orderLines || delivery.orderLines.length === 0)
                .map((delivery) => delivery.time)

            if (invalidTimes.length === 0) {
                return true
            }

            return this.createError({
                message: `${INVALID_DELIVERIES}${invalidTimes.join(",")}`,
            })
        })

    // if (activeFieldKeys.has(DynamicConfigKey.BUYER_PARTIES) && shape[DynamicConfigKey.BUYER_PARTIES]) {
    //     shape[DynamicConfigKey.BUYER_PARTIES] = relaxUnlessInvoice(shape[DynamicConfigKey.BUYER_PARTIES]!)
    // }

    if (!isSpecialOrder) {
        // 2) All accounting dimension keys → also NOT mandatory if ≠ INVOICE
        allFields
            .filter((f) => activeFieldKeys.has(f.key) && isAccountingDimensionKey(f.key))
            .forEach((f) => {
                const k = f.key as ExtendedDynamicConfigKey
                if (shape[k]) {
                    shape[k] = relaxUnlessInvoice(shape[k]!)
                }
            })
    }

    return Yup.object()
        .shape(shape as Record<ExtendedDynamicConfigKey, Yup.Schema<any>>)
        .test(
            "delivery-location-required",
            l10n.getString("create-order-delivery-location-required"),
            function (value) {
                if (!value || typeof value !== "object") {
                    return true
                }

                // If neither field is present in config, do not block the form. Hidden fields count as
                // absent here: nothing renders them in this flow, so requiring a value would strand the
                // error on a field the user cannot see or fill.
                const hasAnyDeliveryLocationField =
                    renderableFieldKeys.has(DynamicConfigKey.DELIVERY_LOCATIONS) ||
                    renderableFieldKeys.has(DynamicConfigKey.OTHER_DELIVERY_LOCATION)

                if (!hasAnyDeliveryLocationField) {
                    return true
                }

                const record = value as Record<string, unknown>
                const type = record[DynamicConfigKey.DELIVERY_TYPE]

                if (type !== DeliveryFulfillmentType.DELIVERY) {
                    return true
                }

                const { isSatisfied, errorPath } = evaluateDeliveryLocation(record)

                return isSatisfied
                    ? true
                    : this.createError({
                          path: errorPath,
                          message: l10n.getString("create-order-delivery-location-required"),
                      })
            },
        )
        .test(
            "meeting-time-required-by-extra-service",
            l10n.getString("create-order-meeting-time-required"),
            function (value) {
                if (!value || typeof value !== "object") return true

                const deliveries = (value as Record<string, unknown>)[DynamicConfigKey.MULTIPLE_DELIVERIES] as
                    | DeliveryTimesField
                    | undefined

                if (!deliveries?.deliveryTimes) return true

                const hasExtraServiceRequiringMeetingTime = deliveries.deliveryTimes.some(
                    (delivery: DeliveryWithCart) =>
                        delivery.orderLines.some(
                            (line) => "isMeetingTimeRequired" in line && line.isMeetingTimeRequired,
                        ),
                )

                if (!hasExtraServiceRequiringMeetingTime) return true

                const meetingStart = (value as Record<string, unknown>)[DynamicConfigKey.MEETING_START_TIME]
                if (meetingStart != null && meetingStart !== "") return true

                return this.createError({
                    path: DynamicConfigKey.MEETING_START_TIME,
                    message: l10n.getString("create-order-meeting-time-required"),
                })
            },
        )
        .test(
            "meeting-end-time-required-by-extra-service",
            l10n.getString("create-order-meeting-time-required"),
            function (value) {
                if (!value || typeof value !== "object") return true

                const deliveries = (value as Record<string, unknown>)[DynamicConfigKey.MULTIPLE_DELIVERIES] as
                    | DeliveryTimesField
                    | undefined

                if (!deliveries?.deliveryTimes) return true

                const hasExtraServiceRequiringMeetingTime = deliveries.deliveryTimes.some(
                    (delivery: DeliveryWithCart) =>
                        delivery.orderLines.some(
                            (line) => "isMeetingTimeRequired" in line && line.isMeetingTimeRequired,
                        ),
                )

                if (!hasExtraServiceRequiringMeetingTime) return true

                const meetingEnd = (value as Record<string, unknown>)[DynamicConfigKey.MEETING_END_TIME]
                if (meetingEnd != null && meetingEnd !== "") return true

                return this.createError({
                    path: DynamicConfigKey.MEETING_END_TIME,
                    message: l10n.getString("create-order-meeting-time-required"),
                })
            },
        )
        .test(
            "meeting-location-required-by-extra-service",
            l10n.getString("create-order-meeting-location-required"),
            function (value) {
                if (!value || typeof value !== "object") return true

                const deliveries = (value as Record<string, unknown>)[DynamicConfigKey.MULTIPLE_DELIVERIES] as
                    | DeliveryTimesField
                    | undefined

                if (!deliveries?.deliveryTimes) return true

                const hasExtraServiceRequiringLocation = deliveries.deliveryTimes.some((delivery: DeliveryWithCart) =>
                    delivery.orderLines.some(
                        (line) => "isMeetingLocationRequired" in line && line.isMeetingLocationRequired,
                    ),
                )

                if (!hasExtraServiceRequiringLocation) return true

                const { isSatisfied, errorPath } = evaluateDeliveryLocation(value as Record<string, unknown>)

                if (isSatisfied) return true

                return this.createError({
                    path: errorPath,
                    message: l10n.getString("create-order-meeting-location-required"),
                })
            },
        )
}
